import { localDate, type AdminAiAssistant } from "@teamna/shared";
import { useState, type ReactNode } from "react";
import { Badge, Button, ConfirmDialog, ErrorState, Field, Panel, SkeletonList, showToast } from "@teamna/ui/components/index.ts";
import { isApiError } from "@teamna/web-client/api/client.ts";
import { formatDate } from "@teamna/web-client/lib/format.ts";
import { useAiAssistantAdmin, useSetAiAssistantGrant } from "../lib/cc-api.ts";

/**
 * AI Assistant of one business (strategy repo `teamna/strategy/ai-assistant-v1.md`, "The package").
 * The owner buys it in Desk (a second item on the Teamna subscription), or Teamna staff give it free
 * here, with an optional last day in the business's time zone. Stop takes back only the free access:
 * a bought package does not change. Each change writes an audit row with the staff email. The
 * act-as AI Assistant page shows this card too (`act-as-assistant.tsx`); act-as never opens a chat,
 * because a conversation belongs to the person who asked.
 */
export function AiAssistantCard({ businessId, timezone }: { businessId: string; timezone: string }) {
  const state = useAiAssistantAdmin(businessId);

  let body: ReactNode;
  if (state.data) body = <CardBody data={state.data} businessId={businessId} timezone={timezone} />;
  else if (state.isLoading) {
    body = (
      <output aria-label="Loading the AI Assistant setting" className="block">
        <SkeletonList rows={1} />
      </output>
    );
  } else body = <ErrorState error={state.error} onRetry={() => void state.refetch()} />;

  return (
    <section aria-labelledby="ai-assistant-heading" className="mb-8 rounded-card border border-line bg-paper p-5 shadow-card">
      <h2 id="ai-assistant-heading" className="mb-3 text-[17px]">
        AI Assistant
      </h2>
      {body}
    </section>
  );
}

/** "2 Sep 2026" for a `yyyy-MM-dd` day. A calendar day has no time zone. */
function dayText(day: string): string {
  return formatDate(`${day}T12:00:00.000Z`, "UTC");
}

/** The state in short lines: bought, given free, both, or off. */
export function aiAssistantLines(data: AdminAiAssistant, timezone: string, today: string): string[] {
  const lines: string[] = [];
  if (data.purchased) {
    const since = data.purchasedSince ? ` since ${formatDate(data.purchasedSince, timezone)}` : "";
    lines.push(`Bought by the business${since}.${data.endsAt ? ` Ends on ${formatDate(data.endsAt, timezone)}.` : ""}`);
  }
  if (data.grant) {
    if (data.grant.until === null) lines.push("Given free by Teamna, with no end date.");
    else if (data.grant.until < today) lines.push(`The free access from Teamna ended on ${dayText(data.grant.until)}.`);
    else lines.push(`Given free by Teamna, last day ${dayText(data.grant.until)}.`);
  }
  if (!data.on) lines.push("Off. The owner sees the offer in Desk.");
  return lines;
}

function errorText(error: unknown, fallback: string): string {
  return isApiError(error) && error.detail ? error.detail : fallback;
}

function CardBody({ data, businessId, timezone }: { data: AdminAiAssistant; businessId: string; timezone: string }) {
  const setGrant = useSetAiAssistantGrant(businessId);
  const [panel, setPanel] = useState<"give" | "change" | null>(null);
  const [stopping, setStopping] = useState(false);
  const today = localDate(new Date(), timezone);

  const stop = () =>
    setGrant.mutate(null, {
      onSuccess: (next) => {
        setStopping(false);
        showToast(next.on ? "The free access stopped. The bought package stays on." : "The free access stopped. AI Assistant is off for this business.");
      },
      onError: (error) => showToast(errorText(error, "The free access did not stop. Try again.")),
    });

  return (
    <>
      <div className="mb-4 flex flex-wrap items-start gap-2">
        <Badge tone={data.on ? "confirmed" : "quiet"}>{data.on ? "On" : "Off"}</Badge>
        <div className="min-w-0 text-[14px]">
          {aiAssistantLines(data, timezone, today).map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {data.grant ? (
          <>
            <Button variant="secondary" onClick={() => setPanel("change")}>
              Change the last day
            </Button>
            <Button variant="secondary" onClick={() => setStopping(true)}>
              Stop the free access
            </Button>
          </>
        ) : (
          <Button variant="secondary" onClick={() => setPanel("give")}>
            Give free
          </Button>
        )}
      </div>

      {panel ? (
        <GrantPanel
          mode={panel}
          businessId={businessId}
          today={today}
          until={data.grant?.until ?? null}
          onClose={() => setPanel(null)}
        />
      ) : null}

      <ConfirmDialog
        open={stopping}
        title="Stop the free access?"
        confirmLabel="Stop the free access"
        tone="danger"
        loading={setGrant.isPending}
        onConfirm={stop}
        onCancel={() => setStopping(false)}
      >
        <p>
          {data.purchased
            ? "The business bought the package, so AI Assistant stays on. The bought package does not change."
            : "The owner and the admins lose AI Assistant now. Their chats stay. A package that the business buys later does not change."}
        </p>
      </ConfirmDialog>
    </>
  );
}

/** Give AI Assistant free, or change the last day of the free access. An empty day has no end. */
function GrantPanel({
  mode,
  businessId,
  today,
  until,
  onClose,
}: {
  mode: "give" | "change";
  businessId: string;
  today: string;
  until: string | null;
  onClose: () => void;
}) {
  const setGrant = useSetAiAssistantGrant(businessId);
  // A last day in the past shows empty: the field only takes today or later.
  const [day, setDay] = useState(until && until >= today ? until : "");
  const title = mode === "give" ? "Give AI Assistant free" : "Change the last day";

  const submit = () =>
    setGrant.mutate(
      { until: day === "" ? null : day },
      {
        onSuccess: () => {
          showToast(day === "" ? "AI Assistant is free for this business, with no end date." : `AI Assistant is free for this business. The last day is ${dayText(day)}.`);
          onClose();
        },
        onError: (error) => showToast(errorText(error, "AI Assistant did not change. Try again.")),
      },
    );

  return (
    <Panel
      open
      onClose={onClose}
      title={title}
      subtitle="The owner and the admins get AI Assistant at no cost. An audit row records your email."
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={setGrant.isPending} onClick={submit}>
            {mode === "give" ? "Give free" : "Save the last day"}
          </Button>
        </>
      }
    >
      <Field
        label="Last day"
        type="date"
        min={today}
        value={day}
        onChange={(event) => setDay(event.target.value)}
        hint="Optional. Leave it empty for no end date. The day is in the business's time zone. After that day, AI Assistant goes off unless the business bought it."
      />
    </Panel>
  );
}
